import { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, parseISO, isWithinInterval } from 'date-fns';
import {
  Calendar,
  DollarSign,
  TrendingUp,
  Download,
  FileText,
  Filter,
  X,
  RefreshCcw,
  Share2,
  Printer,
  SlidersHorizontal,
} from 'lucide-react';
import Papa from 'papaparse';

import { api } from '../services/api';

const QUICK_RANGES = [
  { id: 'next7', label: 'Próx. 7 dias', days: 7 },
  { id: 'next15', label: 'Próx. 15 dias', days: 15 },
  { id: 'next30', label: 'Próx. 30 dias', days: 30 },
  { id: 'next60', label: 'Próx. 60 dias', days: 60 },
];

const TYPE_OPTIONS = [
  { value: 'receita', label: 'Receitas' },
  { value: 'despesa', label: 'Despesas' },
];

const COLORS = {
  receita: '#22c55e',
  despesa: '#ef4444',
  pendente: '#f59e0b',
  pago: '#22c55e',
  pago_parcial: '#0ea5e9',
  liquidado: '#22c55e',
  atrasado: '#ef4444',
  cancelado: '#6b7280',
  aguardando: '#f97316',
};

const normalizeTipo = (tipo) => {
  const value = String(tipo || '').toLowerCase();
  if (value === 'receber') return 'receita';
  if (value === 'pagar') return 'despesa';
  return value;
};

const parseDateSafe = (value) => {
  if (!value) return null;
  try {
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    const normalized = typeof value === 'string' && value.length === 10 ? `${value}T00:00:00` : value;
    return parseISO(normalized);
  } catch (error) {
    return null;
  }
};

const ensureValidDate = (value) => (value instanceof Date && !Number.isNaN(value.getTime()) ? value : null);

const ensureISODate = (value) => {
  const parsed = parseDateSafe(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  return format(parsed, 'yyyy-MM-dd');
};

const formatDateLabel = (value) => {
  const parsed = parseDateSafe(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return '--';
  return format(parsed, 'dd/MM/yyyy');
};

const formatStatusLabel = (value) => {
  const base = String(value || 'Não informado').toLowerCase();
  return base.charAt(0).toUpperCase() + base.slice(1);
};

const formatTipoLabel = (tipo) => {
  const normalized = normalizeTipo(tipo);
  if (normalized === 'receita') return 'Receita';
  if (normalized === 'despesa') return 'Despesa';
  if (!normalized) return 'Não informado';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getEntityName = (lancamento) => {
  const candidates = [
    lancamento.entidade,
    lancamento.empresa,
    lancamento.empresa_nome,
    lancamento.contraparte,
    lancamento.parte_oposta,
    lancamento.cliente,
    lancamento.cliente_nome,
    lancamento.fornecedor,
    lancamento.fornecedor_nome,
    lancamento.emitente,
    lancamento.emitente_nome,
    lancamento.destinatario,
    lancamento.destinatario_nome,
  ];

  const found = candidates.find((item) => typeof item === 'string' && item.trim());
  return found ? found.trim() : 'Não informado';
};

const createInitialFilters = () => {
  const today = new Date();
  const start = ensureISODate(today);
  const end = ensureISODate(addDays(today, 29));
  return {
    periodPreset: 'next30',
    startDate: start,
    endDate: end,
    upcomingDays: 30,
    tipos: ['receita', 'despesa'],
    status: [],
    entidades: [],
  };
};

const normalizeFilterState = (raw) => {
  const draft = {
    periodPreset: raw?.periodPreset || '',
    startDate: raw?.startDate ? ensureISODate(raw.startDate) : '',
    endDate: raw?.endDate ? ensureISODate(raw.endDate) : '',
    upcomingDays: raw?.upcomingDays,
    tipos: Array.isArray(raw?.tipos) ? raw.tipos.map(normalizeTipo).filter(Boolean) : [],
    status: Array.isArray(raw?.status)
      ? raw.status
          .map((item) => String(item || '').toLowerCase())
          .filter((item, index, arr) => item && arr.indexOf(item) === index)
      : [],
    entidades: Array.isArray(raw?.entidades)
      ? raw.entidades.filter((item, index, arr) => item && arr.indexOf(item) === index)
      : [],
  };

  const numericDays = Number(draft.upcomingDays);
  if (Number.isFinite(numericDays) && numericDays > 0) {
    draft.upcomingDays = numericDays;
    const base = draft.startDate ? parseDateSafe(draft.startDate) : new Date();
    const end = addDays(base, numericDays - 1);
    draft.startDate = ensureISODate(base);
    draft.endDate = ensureISODate(end);
  } else {
    draft.upcomingDays = '';
  }

  if (!draft.tipos.length) {
    draft.tipos = ['receita', 'despesa'];
  }

  return draft;
};

const buildQueryParams = (filters) => {
  const params = {};
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.upcomingDays) params.upcomingDays = filters.upcomingDays;
  if (filters.periodPreset) params.periodPreset = filters.periodPreset;
  if (filters.tipos?.length) params.tipo = filters.tipos;
  if (filters.status?.length) params.status = filters.status;
  if (filters.entidades?.length) params.entidade = filters.entidades;
  return params;
};

const applyLancamentoFilters = (items, filters) => {
  if (!Array.isArray(items) || !items.length) return [];
  const tiposSet = new Set((filters.tipos || []).map(normalizeTipo));
  const statusSet = new Set((filters.status || []).map((item) => String(item || '').toLowerCase()));
  const entidadesSet = new Set((filters.entidades || []).map((item) => item.toLowerCase()));
  const startRaw = filters.startDate ? parseDateSafe(filters.startDate) : null;
  const endRaw = filters.endDate ? parseDateSafe(filters.endDate) : null;
  const start = ensureValidDate(startRaw);
  const end = ensureValidDate(endRaw);

  return items.filter((lanc) => {
    const tipo = normalizeTipo(lanc.tipo);
    const status = String(lanc.status || '').toLowerCase();
    const entidade = getEntityName(lanc).toLowerCase();
    const dataReferencia =
      ensureValidDate(parseDateSafe(lanc.data_vencimento)) ||
      ensureValidDate(parseDateSafe(lanc.data_emissao)) ||
      ensureValidDate(parseDateSafe(lanc.data_pagamento));

    const matchesTipo = !tiposSet.size || !tipo || tiposSet.has(tipo);
    const matchesStatus = !statusSet.size || statusSet.has(status);
    const matchesEntidade = !entidadesSet.size || entidadesSet.has(entidade);
    const matchesDate = (() => {
      if (!start && !end) return true;
      if (!dataReferencia) return true;
      let rangeStart = start || dataReferencia;
      let rangeEnd = end || dataReferencia;

      if (rangeStart > rangeEnd) {
        const temp = rangeStart;
        rangeStart = rangeEnd;
        rangeEnd = temp;
      }

      return isWithinInterval(dataReferencia, { start: rangeStart, end: rangeEnd });
    })();

    return matchesTipo && matchesStatus && matchesEntidade && matchesDate;
  });
};

const formatList = (values = []) => {
  if (!values.length) return '';
  if (values.length <= 3) return values.join(', ');
  return `${values.slice(0, 3).join(', ')} +${values.length - 3}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0);

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};
export default function Relatorios() {
  const initialFiltersRef = useRef(createInitialFilters());
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => ({ ...initialFiltersRef.current }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...initialFiltersRef.current }));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef(null);

  const loadData = async (filtersArg = appliedFilters) => {
    try {
      setLoading(true);
      const normalized = normalizeFilterState(filtersArg);
      const params = buildQueryParams(normalized);
      const [, lancamentosData] = await Promise.all([
        api.getDashboardStats(params),
        api.getLancamentos(params),
      ]);
      setLancamentos(Array.isArray(lancamentosData) ? lancamentosData : []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const normalized = normalizeFilterState(initialFiltersRef.current);
    setFilters(normalized);
    setAppliedFilters(normalized);
    loadData(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setFiltersOpen(false);
        setExportMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const filteredLancamentos = useMemo(
    () => applyLancamentoFilters(lancamentos, appliedFilters),
    [lancamentos, appliedFilters],
  );

  const availableStatus = useMemo(() => {
    const map = new Map();
    lancamentos.forEach((item) => {
      const value = String(item.status || '').toLowerCase();
      if (!value) return;
      if (!map.has(value)) {
        map.set(value, formatStatusLabel(value));
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [lancamentos]);

  const availableEntities = useMemo(() => {
    const set = new Set();
    lancamentos.forEach((item) => {
      const entidade = getEntityName(item);
      if (entidade && entidade !== 'Não informado') {
        set.add(entidade);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [lancamentos]);

  const resumoPorTipo = useMemo(() => {
    const resumo = {
      receita: { total: 0, quantidade: 0 },
      despesa: { total: 0, quantidade: 0 },
    };
    filteredLancamentos.forEach((lanc) => {
      const tipo = normalizeTipo(lanc.tipo);
      const valor = Number(lanc.valor) || 0;
      if (tipo === 'receita') {
        resumo.receita.total += valor;
        resumo.receita.quantidade += 1;
      } else if (tipo === 'despesa') {
        resumo.despesa.total += valor;
        resumo.despesa.quantidade += 1;
      }
    });
    return resumo;
  }, [filteredLancamentos]);

  const saldoProjetado = resumoPorTipo.receita.total - resumoPorTipo.despesa.total;

  const contasAtrasadas = useMemo(() => {
    const resumo = { quantidade: 0, total: 0 };
    const hoje = new Date();
    filteredLancamentos.forEach((lanc) => {
      const status = String(lanc.status || '').toLowerCase();
      const data = parseDateSafe(lanc.data_vencimento);
      if (!data) return;
      if (data < hoje && !['pago', 'liquidado', 'concluido', 'cancelado'].includes(status)) {
        resumo.quantidade += 1;
        resumo.total += Number(lanc.valor) || 0;
      }
    });
    return resumo;
  }, [filteredLancamentos]);

  const dadosPorStatus = useMemo(() => {
    const map = new Map();
    filteredLancamentos.forEach((lanc) => {
      const label = formatStatusLabel(lanc.status);
      const value = Number(lanc.valor) || 0;
      if (!map.has(label)) {
        map.set(label, { name: label, value: 0, quantidade: 0 });
      }
      const entry = map.get(label);
      entry.value += value;
      entry.quantidade += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [filteredLancamentos]);

  const resumoDetalhado = useMemo(() => {
    return ['receita', 'despesa'].map((tipo) => {
      const dados = resumoPorTipo[tipo];
      return {
        tipo,
        quantidade: dados.quantidade,
        total: dados.total,
        media: dados.quantidade ? dados.total / dados.quantidade : 0,
      };
    });
  }, [resumoPorTipo]);

  const resumoStatusDetalhado = useMemo(() => {
    return dadosPorStatus.map((item) => ({
      ...item,
      key: item.name.toLowerCase(),
    }));
  }, [dadosPorStatus]);

  const filterChips = useMemo(() => {
    const chips = [];
    if (appliedFilters.startDate || appliedFilters.endDate) {
      const periodLabel = `${formatDateLabel(appliedFilters.startDate)} – ${formatDateLabel(appliedFilters.endDate)}`;
      chips.push(`Período: ${periodLabel}`);
    }
    if (appliedFilters.upcomingDays) {
      chips.push(`Próximos ${appliedFilters.upcomingDays} dias`);
    }
    if (appliedFilters.tipos?.length && appliedFilters.tipos.length < TYPE_OPTIONS.length) {
      chips.push(`Tipos: ${appliedFilters.tipos.map(formatTipoLabel).join(', ')}`);
    }
    if (appliedFilters.status?.length) {
      chips.push(`Status: ${formatList(appliedFilters.status.map(formatStatusLabel))}`);
    }
    if (appliedFilters.entidades?.length) {
      chips.push(`Entidades: ${formatList(appliedFilters.entidades)}`);
    }
    return chips;
  }, [appliedFilters]);

  const toggleArrayValue = (field, value) => {
    setFilters((prev) => {
      const current = new Set(prev[field] || []);
      if (current.has(value)) {
        current.delete(value);
      } else {
        current.add(value);
      }
      return {
        ...prev,
        [field]: Array.from(current),
      };
    });
  };

  const handleQuickRange = (range) => {
    setFilters((prev) => {
      if (prev.periodPreset === range.id) {
        return {
          ...prev,
          periodPreset: '',
          upcomingDays: '',
        };
      }
      const today = new Date();
      return {
        ...prev,
        periodPreset: range.id,
        upcomingDays: range.days,
        startDate: ensureISODate(today),
        endDate: ensureISODate(addDays(today, range.days - 1)),
      };
    });
  };

  const handleApplyFilters = () => {
    const normalized = normalizeFilterState(filters);
    setAppliedFilters(normalized);
    setFilters(normalized);
    setFiltersOpen(false);
    loadData(normalized);
  };

  const handleClearFilters = () => {
    const reset = normalizeFilterState(initialFiltersRef.current);
    setFilters(reset);
    setAppliedFilters(reset);
    setFiltersOpen(false);
    loadData(reset);
  };

  const buildPrintableHtml = () => {
    const filtersSummary = filterChips.length
      ? filterChips
      : ['Nenhum filtro adicional aplicado.'];
    const resumoRows = resumoDetalhado
      .map((item) => {
        const tipo = item.tipo === 'receita' ? 'Receitas' : 'Despesas';
        const badgeClass =
          item.tipo === 'receita'
            ? 'badge badge-receita'
            : item.tipo === 'despesa'
              ? 'badge badge-despesa'
              : 'badge';
        return `
          <tr>
            <td><span class="${badgeClass}">${escapeHtml(tipo)}</span></td>
            <td>${escapeHtml(item.quantidade)}</td>
            <td class="numeric">${escapeHtml(formatCurrency(item.total))}</td>
            <td class="numeric">${escapeHtml(formatCurrency(item.media))}</td>
          </tr>
        `;
      })
      .join('');

    const lancRows = filteredLancamentos
      .map((lanc) => {
        const tipoNormalized = normalizeTipo(lanc.tipo);
        const badgeClass =
          tipoNormalized === 'receita'
            ? 'badge badge-receita'
            : tipoNormalized === 'despesa'
              ? 'badge badge-despesa'
              : 'badge';
        const descricao = lanc.descricao || lanc.nome || lanc.categoria || '—';
        return `
          <tr>
            <td><span class="${badgeClass}">${escapeHtml(formatTipoLabel(lanc.tipo))}</span></td>
            <td>${escapeHtml(getEntityName(lanc))}</td>
            <td>${escapeHtml(descricao)}</td>
            <td>${escapeHtml(formatDateLabel(lanc.data_emissao))}</td>
            <td>${escapeHtml(formatDateLabel(lanc.data_vencimento))}</td>
            <td>${escapeHtml(formatStatusLabel(lanc.status))}</td>
            <td class="numeric">${escapeHtml(formatCurrency(lanc.valor))}</td>
          </tr>
        `;
      })
      .join('');

    const generatedAt = format(new Date(), 'dd/MM/yyyy HH:mm');

    return `<!DOCTYPE html>
<html lang="pt-BR">
  <head>
    <meta charset="utf-8" />
    <title>Relatório Financeiro</title>
    <style>
      body { font-family: 'Inter', Arial, sans-serif; color: #111827; margin: 32px; }
      h1 { font-size: 24px; margin: 0 0 8px; }
      h2 { font-size: 18px; margin: 24px 0 8px; color: #0f172a; }
      p { margin: 4px 0; font-size: 13px; color: #4b5563; }
      ul { margin: 8px 0 0; padding-left: 18px; font-size: 13px; color: #1f2937; }
      table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 12px; }
      th, td { border: 1px solid #e5e7eb; padding: 8px 12px; }
      th { background: #f9fafb; color: #374151; text-transform: uppercase; font-size: 11px; letter-spacing: 0.05em; }
      td { color: #111827; }
      td.numeric { text-align: right; font-weight: 600; }
      .badge { display: inline-block; padding: 4px 10px; border-radius: 9999px; font-weight: 600; font-size: 11px; }
      .badge-receita { background: #dcfce7; color: #15803d; }
      .badge-despesa { background: #fee2e2; color: #b91c1c; }
      .section { margin-top: 32px; }
      .meta { font-size: 12px; color: #6b7280; }
    </style>
  </head>
  <body>
    <header>
      <h1>Relatório Financeiro</h1>
      <p class="meta">Gerado em ${escapeHtml(generatedAt)}</p>
      <p class="meta">Período analisado: ${escapeHtml(formatDateLabel(appliedFilters.startDate))} – ${escapeHtml(formatDateLabel(appliedFilters.endDate))}</p>
    </header>

    <section class="section">
      <h2>Filtros aplicados</h2>
      <ul>
        ${filtersSummary.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}
      </ul>
    </section>

    <section class="section">
      <h2>Resumo por categoria</h2>
      <table>
        <thead>
          <tr>
            <th>Categoria</th>
            <th>Quantidade</th>
            <th>Total</th>
            <th>Média</th>
          </tr>
        </thead>
        <tbody>
          ${resumoRows}
        </tbody>
      </table>
    </section>

    <section class="section">
      <h2>Operações filtradas</h2>
      <table>
        <thead>
          <tr>
            <th>Operação</th>
            <th>Entidade</th>
            <th>Descrição</th>
            <th>Emissão</th>
            <th>Vencimento</th>
            <th>Status</th>
            <th>Valor</th>
          </tr>
        </thead>
        <tbody>
          ${lancRows || '<tr><td colspan="7">Nenhum lançamento encontrado com os filtros atuais.</td></tr>'}
        </tbody>
      </table>
    </section>
  </body>
</html>`;
  };

  const handleExport = async (mode) => {
    try {
      setExporting(true);
      if (mode === 'pdf') {
        const printableHtml = buildPrintableHtml();
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          window.alert('Não foi possível abrir a visualização para impressão. Verifique o bloqueio de pop-ups.');
          return;
        }
        printWindow.opener = null;
        printWindow.document.open();
        printWindow.document.write(printableHtml);
        printWindow.document.close();
        const triggerPrint = () => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        };
        if (printWindow.document.readyState === 'complete') {
          triggerPrint();
        } else {
          printWindow.onload = triggerPrint;
        }
      } else if (mode === 'excel') {
        const rows = filteredLancamentos.map((lanc) => ({
          Tipo: formatTipoLabel(lanc.tipo),
          Entidade: getEntityName(lanc),
          Descrição: lanc.descricao || lanc.nome || '',
          Emissão: formatDateLabel(lanc.data_emissao),
          Vencimento: formatDateLabel(lanc.data_vencimento),
          Status: formatStatusLabel(lanc.status),
          Valor: Number(lanc.valor) || 0,
        }));
        const csv = Papa.unparse(rows);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `relatorio-financeiro-${ensureISODate(new Date())}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else if (mode === 'share') {
        const params = new URLSearchParams();
        const query = buildQueryParams(appliedFilters);
        Object.entries(query).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => params.append(key, item));
          } else if (value !== undefined && value !== null && value !== '') {
            params.append(key, value);
          }
        });
        const shareUrl = `${window.location.origin}${window.location.pathname}${
          params.toString() ? `?${params.toString()}` : ''
        }`;
        if (navigator.share) {
          await navigator.share({
            title: 'Relatório Financeiro',
            text: 'Visualize o relatório conforme os filtros selecionados.',
            url: shareUrl,
          });
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          window.alert('Link copiado para a área de transferência.');
        } else {
          window.prompt('Copie o link do relatório', shareUrl);
        }
      }
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      window.alert('Não foi possível exportar o relatório. Tente novamente.');
    } finally {
      setExporting(false);
      setExportMenuOpen(false);
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando relatórios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in print:animate-none print:space-y-4 print:bg-white print:text-black">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Relatórios</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Construa visões personalizadas das suas operações financeiras
          </p>
        </div>
        <div className="flex gap-3 print:hidden">
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => setFiltersOpen(true)}
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filtros avançados
          </button>
          <div className="relative" ref={exportMenuRef}>
            <button
              className="btn-primary flex items-center gap-2"
              onClick={() => setExportMenuOpen((prev) => !prev)}
              disabled={exporting || !filteredLancamentos.length}
            >
              {exporting ? (
                <>
                  <Printer className="w-5 h-5 animate-pulse" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Exportar
                </>
              )}
            </button>
            {exportMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl z-40">
                <button
                  className="w-full px-4 py-3 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                  onClick={() => handleExport('pdf')}
                >
                  <Printer className="w-4 h-4" />
                  Exportar para PDF / Impressão
                </button>
                <button
                  className="w-full px-4 py-3 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                  onClick={() => handleExport('excel')}
                >
                  <Download className="w-4 h-4" />
                  Exportar planilha (.csv)
                </button>
                <button
                  className="w-full px-4 py-3 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                  onClick={() => handleExport('share')}
                >
                  <Share2 className="w-4 h-4" />
                  Compartilhar relatório
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card print:border print:border-gray-200 print:shadow-none">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 dark:border-gray-700 pb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Filtros aplicados</h3>
          </div>
          <button
            className="btn-link text-sm text-primary-600 dark:text-primary-400 flex items-center gap-1 print:hidden"
            onClick={handleClearFilters}
          >
            <RefreshCcw className="w-4 h-4" />
            Limpar tudo
          </button>
        </div>
        <div className="pt-4 flex flex-wrap gap-2">
          {filterChips.length ? (
            filterChips.map((chip) => (
              <span
                key={chip}
                className="inline-flex items-center gap-1 rounded-full bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-200 px-3 py-1 text-sm font-medium"
              >
                {chip}
              </span>
            ))
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Nenhum filtro adicional aplicado. Exibindo visão padrão dos próximos 30 dias.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card-gradient border-l-4 border-success-500 print:bg-white print:shadow-none">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Total Receitas
            </p>
            <DollarSign className="w-5 h-5 text-success-600 dark:text-success-400" />
          </div>
          <p className="text-3xl font-bold text-success-600 dark:text-success-400 mb-2">
            {formatCurrency(resumoPorTipo.receita.total)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {resumoPorTipo.receita.quantidade} lançamentos
          </p>
        </div>

        <div className="card-gradient border-l-4 border-danger-500 print:bg-white print:shadow-none">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Total Despesas
            </p>
            <DollarSign className="w-5 h-5 text-danger-600 dark:text-danger-400" />
          </div>
          <p className="text-3xl font-bold text-danger-600 dark:text-danger-400 mb-2">
            {formatCurrency(resumoPorTipo.despesa.total)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {resumoPorTipo.despesa.quantidade} lançamentos
          </p>
        </div>

        <div className={`card-gradient border-l-4 ${
          saldoProjetado >= 0 ? 'border-primary-500' : 'border-danger-500'
        } print:bg-white print:shadow-none`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Saldo projetado
            </p>
            <TrendingUp
              className={`w-5 h-5 ${
                saldoProjetado >= 0 ? 'text-primary-600 dark:text-primary-400' : 'text-danger-600 dark:text-danger-400'
              }`}
            />
          </div>
          <p
            className={`text-3xl font-bold mb-2 ${
              saldoProjetado >= 0 ? 'text-primary-600 dark:text-primary-400' : 'text-danger-600 dark:text-danger-400'
            }`}
          >
            {formatCurrency(saldoProjetado)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {saldoProjetado >= 0 ? 'Positivo' : 'Negativo'}
          </p>
        </div>

        <div className="card-gradient border-l-4 border-warning-500 print:bg-white print:shadow-none">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Contas Atrasadas
            </p>
            <Calendar className="w-5 h-5 text-warning-600 dark:text-warning-400" />
          </div>
          <p className="text-3xl font-bold text-warning-600 dark:text-warning-400 mb-2">
            {contasAtrasadas.quantidade}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {formatCurrency(contasAtrasadas.total)}
          </p>
        </div>
      </div>

      <div className="card p-0 overflow-hidden print:border print:border-gray-200 print:shadow-none">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            Resumo Detalhado
          </h3>
        </div>
        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Quantidade
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Média
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {resumoDetalhado.map((item) => (
                <tr key={item.tipo} className="table-row">
                  <td className="px-6 py-4">
                    <span
                      className={`badge ${
                        item.tipo === 'receita' ? 'badge-success' : 'badge-danger'
                      }`}
                    >
                      {item.tipo === 'receita' ? 'Receitas' : 'Despesas'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-900 dark:text-white font-semibold">
                    {item.quantidade}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`font-bold text-lg ${
                        item.tipo === 'receita'
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-danger-600 dark:text-danger-400'
                      }`}
                    >
                      {formatCurrency(item.total)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-900 dark:text-white font-semibold">
                    {formatCurrency(item.media)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card p-0 overflow-hidden print:border print:border-gray-200 print:shadow-none">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              Operações filtradas
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Visualize cada lançamento com os filtros aplicados e gere relatórios prontos para impressão.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {resumoStatusDetalhado.map((item) => (
              <span
                key={`status-pill-${item.key}`}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: COLORS[item.key] || COLORS[normalizeTipo(item.key)] || '#3b82f6' }}
                />
                {item.name}
                <span className="font-semibold">{formatCurrency(item.value)}</span>
              </span>
            ))}
            {!resumoStatusDetalhado.length && (
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-400">
                Nenhum status no filtro atual
              </span>
            )}
          </div>
        </div>
        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Operação</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Entidade</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Emissão</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Vencimento</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLancamentos.map((lanc, index) => (
                <tr key={lanc.id || `${lanc.tipo}-${lanc.data_vencimento}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-2 text-sm font-semibold ${
                        normalizeTipo(lanc.tipo) === 'receita'
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-danger-600 dark:text-danger-400'
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full bg-current" />
                      {formatTipoLabel(lanc.tipo)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-900 dark:text-white">{getEntityName(lanc)}</td>
                  <td className="px-6 py-4 text-gray-700 dark:text-gray-300">
                    {lanc.descricao || lanc.nome || lanc.categoria || '—'}
                  </td>
                  <td className="px-6 py-4 text-gray-700 dark:text-gray-300">{formatDateLabel(lanc.data_emissao)}</td>
                  <td className="px-6 py-4 text-gray-700 dark:text-gray-300">{formatDateLabel(lanc.data_vencimento)}</td>
                  <td className="px-6 py-4 text-gray-700 dark:text-gray-300">{formatStatusLabel(lanc.status)}</td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(lanc.valor)}
                  </td>
                </tr>
              ))}
              {!filteredLancamentos.length && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    Nenhum lançamento encontrado com os filtros atuais. Ajuste os critérios e tente novamente.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm print:hidden">
          <div className="w-full max-w-md h-full overflow-y-auto bg-white dark:bg-gray-900 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Filtros avançados</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Combine múltiplos critérios para personalizar o relatório.
                </p>
              </div>
              <button
                onClick={() => setFiltersOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Fechar filtros"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-6 space-y-6">
              <section>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                  Intervalos rápidos
                </h3>
                <div className="flex flex-wrap gap-2">
                  {QUICK_RANGES.map((range) => (
                    <button
                      key={range.id}
                      onClick={() => handleQuickRange(range)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                        filters.periodPreset === range.id
                          ? 'border-primary-500 bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-200'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </section>

              <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Data inicial
                  </label>
                  <input
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(event) => setFilters((prev) => ({ ...prev, startDate: event.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Data final
                  </label>
                  <input
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(event) => setFilters((prev) => ({ ...prev, endDate: event.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Próximos (dias)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={filters.upcomingDays || ''}
                    onChange={(event) => setFilters((prev) => ({ ...prev, upcomingDays: event.target.value }))}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  />
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">Tipos</h3>
                <div className="flex flex-wrap gap-2">
                  {TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => toggleArrayValue('tipos', option.value)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                        filters.tipos?.includes(option.value)
                          ? 'border-primary-500 bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-200'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">Status</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {availableStatus.map((status) => (
                    <label
                      key={status.value}
                      className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={filters.status?.includes(status.value)}
                        onChange={() => toggleArrayValue('status', status.value)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>{status.label}</span>
                    </label>
                  ))}
                  {!availableStatus.length && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 col-span-2">
                      Nenhum status disponível na base atual.
                    </p>
                  )}
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                  Clientes / Fornecedores
                </h3>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  {availableEntities.map((entidade) => (
                    <label
                      key={entidade}
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span>{entidade}</span>
                      <input
                        type="checkbox"
                        checked={filters.entidades?.includes(entidade)}
                        onChange={() => toggleArrayValue('entidades', entidade)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </label>
                  ))}
                  {!availableEntities.length && (
                    <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      Nenhuma entidade disponível. Carregue lançamentos para habilitar este filtro.
                    </p>
                  )}
                </div>
              </section>
            </div>

            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                className="btn-secondary flex items-center gap-2"
                onClick={handleClearFilters}
              >
                <RefreshCcw className="w-4 h-4" />
                Limpar filtros
              </button>
              <div className="flex gap-2">
                <button
                  className="btn-outline"
                  onClick={() => setFiltersOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  className="btn-primary"
                  onClick={handleApplyFilters}
                >
                  Aplicar filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
